// src/utils/wind.js
// =====================  WHERE THIS GOES  =====================
// New file. Pure wind-domain math — NO React, NO THREE (mirrors the
// role of solar.js). windScene.js and WindPanel.jsx import from here.
//
// THE ONE CONVENTION THAT MATTERS:
//   NASA POWER WD10M — and every wind rose a user has ever read — is
//   METEOROLOGICAL: degrees clockwise from North for the direction the
//   wind blows *FROM* (the source). A "225°" wind is a SW wind: it comes
//   from the south-west and travels toward the north-east.
//   So everywhere in the app, `dir` = the FROM bearing (source).
//   The scene converts it to a TRAVEL vector (source -> negated) using
//   the empirical north/east basis. windTravelBearing() is the scalar
//   twin of that (from -> from+180). Keep this the single source of
//   truth so the arrow, the tint and the rose never disagree.
// ============================================================

export const RHO_AIR = 1.225;            // kg/m^3, sea-level ~15°C
export const BETZ = 0.593;               // theoretical max Cp
// Rayleigh energy-pattern factor  Ke = <v^3>/<v>^3 = 6/pi ≈ 1.91.
// We only get a MONTHLY MEAN speed from climatology; energy scales with
// v^3, and mean(v^3) != mean(v)^3, so multiplying by Ke recovers the
// distribution's cube-energy instead of under-reading by ~2x.
export const KE_RAYLEIGH = 6 / Math.PI;

// ---------------------------------------------------------------------------
// direction helpers (all in FROM / meteorological convention)
// ---------------------------------------------------------------------------
const SECTORS16 = ['N','NNE','NE','ENE','E','ESE','SE','SSE','S','SSW','SW','WSW','W','WNW','NW','NNW'];

export function sectorIndex16(deg) {
  const d = ((deg % 360) + 360) % 360;
  return Math.round(d / 22.5) % 16;
}
export function dirLabel16(deg) { return SECTORS16[sectorIndex16(deg)]; }

// scalar twin of the travel vector: the bearing the wind blows TOWARD.
export function windTravelBearing(fromDeg) { return (((fromDeg + 180) % 360) + 360) % 360; }

// ---------------------------------------------------------------------------
// pressure / exposure colour ramp  (leeward/sheltered -> windward/exposed)
// blue (low) -> teal -> amber -> orange -> red (high).  t in [0,1].
// ---------------------------------------------------------------------------
const PRESSURE_STOPS = [
  [0x2f, 0x6f, 0xed], [0x2a, 0x9d, 0x8f], [0xe6, 0xc3, 0x4d], [0xef, 0x6c, 0x3a], [0xc0, 0x39, 0x2b],
];
function lerpStops(t) {
  const x = Math.max(0, Math.min(1, t)) * (PRESSURE_STOPS.length - 1);
  const i = Math.floor(x), f = x - i;
  const a = PRESSURE_STOPS[i], b = PRESSURE_STOPS[Math.min(i + 1, PRESSURE_STOPS.length - 1)];
  return [a[0] + (b[0] - a[0]) * f, a[1] + (b[1] - a[1]) * f, a[2] + (b[2] - a[2]) * f];
}
// for scene tint (THREE .setRGB wants 0..1)
export function pressureRGB(t) { const c = lerpStops(t); return [c[0] / 255, c[1] / 255, c[2] / 255]; }
// for panel bars (CSS)
export function pressureCss(t) { const c = lerpStops(t).map((v) => Math.round(v)); return `rgb(${c[0]},${c[1]},${c[2]})`; }

export const ROLE_LABEL = {
  windward: 'Windward', shelteredWindward: 'Windward (sheltered)', side: 'Side', leeward: 'Leeward',
};

// ---------------------------------------------------------------------------
// rooftop wind-energy screening estimate
// Feasibility triage only: regional 10 m speed, assumed Rayleigh distribution,
// no measured on-site data and no turbine power curve / cut-in-cut-out.
// Good for ranking orientations & sanity-checking viability, NOT a yield figure.
// ---------------------------------------------------------------------------
export function scaleToHub(v10, hubH, alpha = 0.27) {
  return v10 * Math.pow(Math.max(hubH, 1) / 10, alpha);   // power-law wind profile
}

// mean electrical power (W) for a monthly-mean 10 m speed
export function meanPowerW(vMean10, opts = {}) {
  const { rotorD = 3, cp = 0.35, eta = 0.85, hubH = 10, alpha = 0.27, roofSpeedup = 1 } = opts;
  const vHub = scaleToHub(vMean10, hubH, alpha) * roofSpeedup;
  const A = Math.PI * (rotorD / 2) ** 2;
  const cpEff = Math.min(cp, BETZ);
  return 0.5 * RHO_AIR * A * cpEff * eta * KE_RAYLEIGH * Math.pow(vHub, 3);
}

export function monthlyEnergyKWh(vMean10, opts = {}) {
  const days = opts.days ?? 30.4;
  return (meanPowerW(vMean10, opts) * days * 24) / 1000;
}

// perMonth: [{ month, prevailingDir, meanSpeed }] -> annual kWh + monthly array
export function annualEnergy(perMonth, opts = {}) {
  const DIM = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  const monthly = perMonth.map((m) =>
    (m.meanSpeed != null ? monthlyEnergyKWh(m.meanSpeed, { ...opts, days: DIM[m.month] ?? 30.4 }) : null));
  const annualKWh = monthly.reduce((s, v) => s + (v || 0), 0);
  return { monthly, annualKWh };
}

// ---------------------------------------------------------------------------
// panel-side rose: aggregate the 12 monthly PREVAILING dirs into 16 sectors,
// magnitude = mean speed. This is a "months-by-prevailing-direction" rose,
// NOT a true frequency rose (climatology can't give that) — label it honestly.
// ---------------------------------------------------------------------------
export function roseFromMonths(perMonth) {
  const bins = Array.from({ length: 16 }, () => ({ count: 0, speedSum: 0 }));
  perMonth.forEach((m) => {
    if (m.prevailingDir == null || m.meanSpeed == null) return;
    const b = bins[sectorIndex16(m.prevailingDir)];
    b.count += 1; b.speedSum += m.meanSpeed;
  });
  const maxCount = Math.max(1, ...bins.map((b) => b.count));
  return bins.map((b, i) => ({
    sector: i, label: SECTORS16[i], count: b.count,
    meanSpeed: b.count ? b.speedSum / b.count : 0, frac: b.count / maxCount,
  }));
}

// pull the prevailing dir + speed for a given month index from a rose payload
export function prevailingForMonth(rose, month) {
  if (!rose?.perMonth?.length) return null;
  return rose.perMonth.find((m) => m.month === month) || null;
}
